import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  IconButton,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Add as AddIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@material-ui/icons';

import { useFileUploader } from '../../secure-portal/documents/components/DocumentFolderUpload';
import api, { FetchError } from '../api/api';
import { Attachment } from '../attachment/Attachment.interface';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import DragArea from '../views/DragArea';
import WrappedTableCell from '../WrappedTableCell';

const StyledTableContainer = styled(TableContainer)`
  border-radius: 2px;
  border: 1px solid ${props => props.theme.palette.divider};
  .row-divider {
    background-color: ${props => props.theme.palette.grey[100]};
    .MuiTableCell-root {
      padding-top: 0px;
      padding-bottom: 0px;
    }
  }
`;

const StyledActions = styled.div`
  padding: 8px 16px;
  & > *:not(:last-child) {
    margin-right: 8px;
  }
`;

// NOTE: taken from https://ux.stackexchange.com/a/13852
// kilobytes (1000) prefixed with lowercased "k-" (i.e. kB)
// kibibytes (1024) prefixed with "K-" or "Ki-" (i.e. KB, KiB)
function formatFileSize(value: number | undefined, type: 'kB' | 'KB') {
  // only show type (kB, KB) if value exists
  if (typeof value !== 'number') {
    return '';
  }
  const bytes = type === 'kB' ? 1000 : 1024;
  // show decimals to also handle sizes less than a kilobyte (e.g. 0.56 kB)
  const result = (value / bytes).toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  });
  return `${result} ${type}`;
}

export interface InputFileProps {
  dragLabel?: React.ReactNode;
  values?: Attachment[];
  compact?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  isLoading?: boolean;
  token?: string;
  inputType?: string;
  uploadPath?: string;
  className?: string;
  onChange?: (values: Attachment[]) => void;
  setTouched?: (touched: boolean) => void;
}

const InputFile: React.FC<InputFileProps> = props => {
  const {
    dragLabel,
    values,
    compact,
    disabled,
    multiple,
    isLoading,
    token,
    inputType,
    uploadPath,
    className,
    onChange,
    setTouched
  } = props;
  const valuesRef = useRef(values);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const hasUploadingRef = useRef(false);
  const [dragOver, setDragOver] = useState(false);
  // states used for rendering items only
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [valuesState, setValuesState] = useState(values || []);

  const [cancelFile, setCancelFile] = useState<File>();
  const [fileToRemove, setFileToRemove] = useState<Attachment>();
  const [attemptedFileNames, setAttemptedFileNames] = useState<string[]>([]);
  const [openFileTypeConfirmDialog, setOpenFileTypeConfirmDialog] =
    useState(false);
  const uploader = useFileUploader(uploadPath, token);
  const cancelItem = cancelFile ? uploader.find(cancelFile) : undefined;
  hasUploadingRef.current =
    uploader.items.length > 0 &&
    uploader.items.some(item => uploader.isUploading(item.status));
  const colSpan = compact ? 2 : 4;
  const showTable = (values && values.length > 0) || uploadFiles.length > 0;
  const showRowDivider = values && values.length > 0 && uploadFiles.length > 0;
  const displayAttemptedFileNames = attemptedFileNames
    .map(name => `"${name}"`)
    .join(', ');
  const tableClasses = ['input-file-table'];
  !showTable && tableClasses.push('hidden');

  const update = (values: Attachment[]) => {
    valuesRef.current = values;
    setValuesState(values);
    onChange?.(values);
  };

  const uploadAll = async (files: File[]) => {
    // upload files, get successful uploads only
    const allUploadedItems = await uploader.uploadAsync(files);
    const uploadedItems = allUploadedItems.filter(
      item => item.status === 'uploaded'
    );
    // do nothing if no uploaded files
    if (uploadedItems.length === 0) {
      return;
    }
    const uploadedFiles: Attachment[] = uploadedItems.map(item => ({
      url: item.presignedUrl?.storageKey,
      signedUrl: item.presignedUrl?.signedUrl,
      name: item.file.name,
      size: item.file.size
    }));
    console.debug('[input-file] uploaded: %o', uploadedFiles);
    // use latest items state and filter only uploaded files
    const values = uploadedFiles.concat(
      (valuesRef.current || []).filter(value => {
        // remove duplicate files
        return !uploadedFiles.some(uploaded => value.name === uploaded.name);
      })
    );
    console.debug(
      '[input-file] update\n  old values: %o\n  new values: %o',
      valuesRef.current,
      values
    );

    // save new uploaded files
    update(values);
    // remove saved items from uploaded
    setUploadFiles(files => {
      return files.filter(file => {
        return !uploadedItems.some(item => item.file === file);
      });
    });
    // remove uploaded items from uploader, make sure it does not abort
    for (const item of uploadedItems) {
      item.abort = undefined;
      uploader.remove(item.file);
    }
  };

  // start uploading file(s)
  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      return;
    }
    setTouched?.(true);
    const files = Array.from(fileList);
    if (!multiple && uploader.items.length > 0) {
      // if only one file allowed, remove it before uploading new file
      uploader.clear();
      setUploadFiles([]);
    }

    const filesToUpload: File[] = [];
    const attemptedFileNames: string[] = [];
    for (const file of files) {
      // save file to upload if valid extension
      const extension = file.name.match(/\.([^.]+)$/g);
      if (!inputType || inputType.includes(extension ? extension[0] : ' ')) {
        filesToUpload.push(file);
      } else if (multiple || attemptedFileNames.length === 0) {
        // for invalid extensions,
        // only push the first file.name for single uploads
        attemptedFileNames.push(file.name);
      }

      // stop immediately for single uploads once there is at least 1 valid file
      if (!multiple && filesToUpload.length > 0) {
        break;
      }
    }

    // only show attempted file names for multiple uploads
    // or, for single uploads, if there are no uploads
    if (
      attemptedFileNames.length > 0 &&
      (multiple || filesToUpload.length === 0)
    ) {
      setAttemptedFileNames(attemptedFileNames);
    }

    // if the filesToUpload array has at least one file, proceed to upload
    if (filesToUpload.length > 0) {
      // add to currently uploading files
      setUploadFiles(files => files.concat(filesToUpload));
      uploadAll(filesToUpload);
    }
  };

  // sync values state
  useEffect(() => {
    // skip updating values state if currently uploading
    if (hasUploadingRef.current) {
      console.debug(
        '[input-file] skip\n  values: %o\n  current: %o',
        values,
        valuesRef.current
      );
      return;
    }
    const valuesState = values || [];
    setValuesState(valuesState);
    // NOTE: avoid setting valuesRef outside useEffect since it
    // may change when component re-renders from progress loading
    valuesRef.current = valuesState;
  }, [values]);

  const loadingRow = isLoading && (
    <TableRow>
      <TableCell colSpan={colSpan} padding='none'>
        <LinearProgress />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <DragArea
        disabled={disabled}
        dragOver={dragOver}
        hideArea={showTable}
        className={className}
        dragLabel={
          dragLabel || (!multiple ? 'Drag a file here to upload' : undefined)
        }
        buttonLabel={!multiple ? 'Select File' : undefined}
        onUpload={handleFiles}
        setDragOver={setDragOver}
        onClick={() => fileInputRef.current?.click()}
      >
        <StyledTableContainer className={tableClasses.join(' ')}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>File Name</TableCell>
                {!compact && (
                  <>
                    <TableCell>Size</TableCell>
                    <TableCell>Download</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {!showRowDivider && loadingRow}
              {showRowDivider && (
                <TableRow className='row-divider'>
                  <TableCell colSpan={colSpan}>
                    <Typography variant='overline'>Uploading</Typography>
                  </TableCell>
                </TableRow>
              )}
              {uploadFiles.map((file, index) => {
                const item = uploader.find(file);
                return (
                  <InputFileItem
                    key={`${file.name}-${file.size}-${index}`}
                    compact={compact}
                    error={item?.error}
                    disabled={disabled}
                    uploading={!!item && uploader.isUploading(item.status)}
                    value={{
                      name: file.name,
                      size: file.size,
                      progress: item?.progress,
                      url: item?.presignedUrl?.storageKey
                    }}
                    onRemove={() => setCancelFile(file)}
                  />
                );
              })}
              {showRowDivider && (
                <TableRow className='row-divider'>
                  <TableCell colSpan={colSpan}>
                    <Typography variant='overline'>Uploaded</Typography>
                  </TableCell>
                </TableRow>
              )}
              {showRowDivider && loadingRow}
              {valuesState.map((file, index) => (
                <InputFileItem
                  key={`${file.name}-${file.size}-${index}`}
                  value={file}
                  compact={compact}
                  // disable remove button when saving
                  disabled={disabled || isLoading}
                  onRemove={() => setFileToRemove(file)}
                />
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>

        <input
          hidden
          type='file'
          accept={inputType}
          ref={fileInputRef}
          multiple={multiple}
          onChange={event => handleFiles(event.target.files)}
        />
      </DragArea>

      {multiple && showTable && (
        <StyledActions>
          <Button
            color='primary'
            variant='outlined'
            disabled={disabled}
            startIcon={<AddIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Add File{multiple && 's'}
          </Button>
          <Typography variant='overline'>
            or drag {multiple ? 'files' : 'a file'} into the table above.
          </Typography>
        </StyledActions>
      )}

      <ConfirmDialog
        open={openFileTypeConfirmDialog}
        title='Invalid file extension'
        message={`The file(s) ${displayAttemptedFileNames} do not have accepted file types. Please contact your administrator.`}
        onCancel={() => setOpenFileTypeConfirmDialog(false)}
        onConfirm={() => setOpenFileTypeConfirmDialog(false)}
      />
      <ConfirmDialog
        open={!!cancelItem}
        title='Cancel File Upload'
        confirmLabel='Cancel Upload'
        message={
          `The file "${cancelItem?.file.name || '?'}" is currently uploading.` +
          ' Are you sure you want to cancel the upload? This action cannot be undone.'
        }
        onCancel={() => setCancelFile(undefined)}
        onConfirm={() => {
          setCancelFile(undefined);
          if (!cancelItem) {
            return;
          }
          uploader.remove(cancelItem.file);
          setUploadFiles(files => {
            return files.filter(file => file !== cancelItem.file);
          });
        }}
      />
      <ConfirmDialog
        open={!!fileToRemove}
        title='Remove File'
        confirmLabel='Remove'
        message={`The file "${
          fileToRemove?.name || '?'
        }" will be removed. This action cannot be undone.`}
        onCancel={() => setFileToRemove(undefined)}
        onConfirm={() => {
          setFileToRemove(undefined);
          if (!fileToRemove) {
            return;
          }
          // abort file upload
          const newValues = (valuesRef.current || []).filter(
            value => value !== fileToRemove
          );
          update(newValues);
          setTouched?.(true);
        }}
      />
    </>
  );
};

export default InputFile;

const StyledTableRow = styled(TableRow)`
  .remove-cell {
    width: 5px;
    padding: 2px 0 2px 8px;
  }
  .actions {
    padding-top: 2px;
    padding-bottom: 2px;
  }
  .error-icon {
    color: ${props => props.theme.palette.error.main};
  }
`;

interface InputFileItemProps {
  value: {
    name?: string;
    size?: number;
    progress?: number;
    url?: string; // storageKey
  };
  error?: FetchError;
  compact?: boolean;
  disabled?: boolean;
  uploading?: boolean;
  onRemove?: () => void;
}

const InputFileItem: React.FC<InputFileItemProps> = props => {
  const { value, error, compact, disabled, uploading, onRemove } = props;
  const [downloading, setDownloading] = useState(false);
  const { url: storageKey } = value;
  const name = value.name || '';
  const size = formatFileSize(value.size, 'KB');

  const downloadFile = async () => {
    if (!storageKey) {
      return;
    }
    setDownloading(true);
    const params = new URLSearchParams({ storageKey }).toString();
    const urlName = name && `/${encodeURI(name)}`;
    const request = api.get(`files/proxy${urlName}?${params}`, {});
    const response = await request.response;
    const blob = await response.blob();
    setDownloading(false);
    saveAs(blob, name);
  };

  return (
    <StyledTableRow>
      <TableCell className='remove-cell'>
        <IconButton
          size='small'
          aria-label='delete'
          disabled={disabled || downloading}
          onClick={() => onRemove?.()}
        >
          <CloseIcon />
        </IconButton>
      </TableCell>
      <WrappedTableCell>
        {!compact || !storageKey ? (
          name
        ) : !downloading ? (
          <Link
            download
            style={{ cursor: 'pointer' }}
            onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
              event.stopPropagation();
              downloadFile();
            }}
          >
            {name}
          </Link>
        ) : (
          <Typography variant='body2' color='textSecondary'>
            {name}
          </Typography>
        )}
      </WrappedTableCell>
      {!compact && (
        <>
          <WrappedTableCell title={size}>{size}</WrappedTableCell>
          <TableCell className='actions'>
            {error ? (
              <Tooltip arrow placement='top' title={error.message}>
                <IconButton onClick={event => event.stopPropagation()}>
                  <ErrorOutlineIcon className='error-icon' />
                </IconButton>
              </Tooltip>
            ) : storageKey && !uploading ? (
              <Button
                color='primary'
                disabled={downloading}
                onClick={event => {
                  event.stopPropagation();
                  downloadFile();
                }}
              >
                Download
              </Button>
            ) : (
              <LinearProgress
                color='primary'
                variant='determinate'
                value={value.progress || 0}
              />
            )}
          </TableCell>
        </>
      )}
    </StyledTableRow>
  );
};
